/* eslint-disable react-hooks/exhaustive-deps */
import { Box, InputAdornment, Typography } from '@mui/material';
import { useEffect, useState, useContext, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { ApprovalStatusLabel } from '../../../../../../api/enumerations';
import { listNoticeCompanies } from '../../../../../../api/theHive/notices';
import {
  ListNoticeCompaniesParams,
  NoticeCompaniesData,
} from '../../../../../../api/theHive/notices/types';
import { IconSearchMS } from '../../../../../../constants/icons';
import { GlobalContext } from '../../../../../../context/global';
import useErrorMessage from '../../../../../../hooks/useErrorMessage';
import { CompaniesTable } from '../../components/CompaniesTable';
import { StyledInputBase } from '../../styles';
import { TableSection, AddButton } from '../styles';

export function NoticeDetailsLicensed(): JSX.Element {
  const [searchTerm, setSearchTerm] = useState('');
  const [companies, setCompanies] = useState<NoticeCompaniesData[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [pageCompanies, setPageCompanies] = useState(1);
  const [totalPagesCompanies, setTotalPagesCompanies] = useState(0);
  const itemsPerPage = 10;

  const { setOpenSnackbar, setErrorMessage, setSnackbarMessage } =
    useContext(GlobalContext);
  const navigate = useNavigate();
  const { notice: noticeIdParam } = useParams();
  const noticeId = parseInt(noticeIdParam as string, 10);
  const { getErrorMessage } = useErrorMessage();
  const searchTimeout = useRef<NodeJS.Timeout>();

  const fetchCompanies = async (search: string): Promise<void> => {
    setIsSearching(true);
    try {
      const params: ListNoticeCompaniesParams = {
        publicNoticeId: noticeId,
        page: pageCompanies,
        size: itemsPerPage,
        search,
        approvalStatus: [ApprovalStatusLabel.APPROVED],
        orderBy: 'approved_at',
      };
      const response = await listNoticeCompanies(params);

      if (response.detail.description)
        throw new Error(response.detail.description);

      if (!response.data) throw new Error('Algo deu errado, tente novamente.');

      setCompanies(response.data);
      setTotalPagesCompanies(response.detail.total_pages);
    } catch (error) {
      setSnackbarMessage(getErrorMessage(error));
      setErrorMessage(true);
      setOpenSnackbar(true);
    } finally {
      setIsSearching(false);
    }
  };

  const handleSearchInputChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    const newSearchTerm = event.target.value;
    setSearchTerm(newSearchTerm);
    setPageCompanies(1);

    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }

    searchTimeout.current = setTimeout(() => {
      fetchCompanies(newSearchTerm);
    }, 600);
  };

  useEffect(() => {
    fetchCompanies(searchTerm);
  }, [pageCompanies]);

  const navigateToAddCompany = (): void => navigate('./add-company');

  return (
    <TableSection>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Typography color="common.grey" fontSize={21} fontWeight={600}>
          Credenciados
        </Typography>
        <Box display="flex" mb={3}>
          <StyledInputBase
            placeholder="buscar empresa"
            inputProps={{ 'aria-label': 'pesquisar', maxLength: 14 }}
            value={searchTerm}
            disabled={isSearching}
            onChange={handleSearchInputChange}
            sx={{ height: '56px', mr: 5 }}
            startAdornment={
              <InputAdornment
                position="start"
                sx={(theme) => ({ color: theme.palette.secondary.main })}
              >
                {IconSearchMS}
              </InputAdornment>
            }
          />
          <AddButton disabled={isSearching} onClick={navigateToAddCompany}>
            adicionar empresa
          </AddButton>
        </Box>
      </Box>
      <CompaniesTable
        data={companies}
        page={pageCompanies}
        totalPages={totalPagesCompanies}
        setPage={setPageCompanies}
        isLoading={isSearching}
      />
    </TableSection>
  );
}
